/**
 * Companion mode: the store builds of Lighthouse (iOS and Android) are a free companion to the web product.
 * Apple 3.1.3(f) and Google Play's consumption-only rule let us ship that way only if the app never sells,
 * never signs anyone up and never points users at a purchase. Every one of those rules lives here, so
 * screens ask `companionPolicy` (or `isCompanionApp()`) instead of scattering `isNativePlatform()` checks.
 *
 * `?companion=1` (kept for the session) turns companion mode on in a web browser for QA.
 */
import { isNativePlatform } from '@/utils/platform'

const QA_FLAG_KEY = 'lh.companionQa'

function hasQaFlag(): boolean {
    if (typeof window === 'undefined') return false
    try {
        const param = new URLSearchParams(window.location.search).get('companion')
        if (param === '1') window.sessionStorage.setItem(QA_FLAG_KEY, '1')
        if (param === '0') window.sessionStorage.removeItem(QA_FLAG_KEY)
        return window.sessionStorage.getItem(QA_FLAG_KEY) === '1'
    } catch {
        return false
    }
}

export function isCompanionApp(): boolean {
    return isNativePlatform() || hasQaFlag()
}

/** What the app may show. Each flag is false in companion mode and true on the web. */
export const companionPolicy = {
    /** Account creation, "Don't have an account?", invite-request copy. */
    get allowSignup() {
        return !isCompanionApp()
    },
    /** Checkout, billing portal, past-due and grace banners, cancel/discount modals, Stripe fetches. */
    get allowBilling() {
        return !isCompanionApp()
    },
    /** Prices, plans, trials, "upgrade", "add more usage", auto-recharge. */
    get allowPricingCopy() {
        return !isCompanionApp()
    },
    /** "Get $100" referrals and Stripe Connect payouts. */
    get allowReferrals() {
        return !isCompanionApp()
    },
    /** The onboarding guide / rail (it ends in a plan picker). */
    get allowOnboarding() {
        return !isCompanionApp()
    },
    /** Meta Pixel, track* conversion calls, Vercel analytics. */
    get allowMarketingPixels() {
        return !isCompanionApp()
    },
    /** LogRocket session replay (decision D-6: off in the app). */
    get allowSessionReplay() {
        return !isCompanionApp()
    },
    /** Calendar and Instagram connect flows (decision D-9: set up on the web). Inbox reconnect stays. */
    get allowIntegrationSetup() {
        return !isCompanionApp()
    },
}
